import { manifest as anthropic } from "./aiProviders/anthropic/manifest";
import { manifest as azure } from "./aiProviders/azure/manifest";
import { manifest as bedrockAnthropic } from "./aiProviders/bedrockAnthropic/manifest";
import { indexAIProvidersById } from "./aiProviders/manifest";
import { manifest as openAI } from "./aiProviders/openai/manifest";
import { manifest as openAICompatible } from "./aiProviders/openaiCompatible/manifest";
import { manifest as oracleOci } from "./aiProviders/oracleOci/manifest";

export {
  availableEffortLevels,
  effortLevels,
  type EffortLevel,
  isEffortLevel,
} from "./aiProviders/effort";

const aiProviders = [
  openAI,
  anthropic,
  bedrockAnthropic,
  azure,
  oracleOci,
  openAICompatible,
] as const;

export type AIProviderId = (typeof aiProviders)[number]["id"];

const aiProvidersById = indexAIProvidersById(aiProviders);

export const configurableAIProviders = aiProviders.filter(
  (provider) => provider.configurable
);

type ConfigurableAIProviderId = (typeof configurableAIProviders)[number]["id"];

export const configurableAIProviderIds = configurableAIProviders.map(
  ({ id }) => id
) as [ConfigurableAIProviderId, ...ConfigurableAIProviderId[]];

// Sentinel for a per-user model preference that pins the env-managed provider
// rather than any configured `ai_providers` row.
export const MANAGED_AI_PROVIDER_ID = "managed";

const managedAnthropicProvider = {
  provider: "anthropic",
  chatModels: ["claude-opus-4-8", "claude-sonnet-5"],
  fastModel: "claude-haiku-4-5-20251001",
} as const;

const managedOpenAIProvider = {
  provider: "openai",
  chatModels: ["gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna"],
  fastModel: "gpt-5.6-luna",
} as const;

const managedAIProviderCatalog = [
  managedAnthropicProvider,
  managedOpenAIProvider,
] as const;

export const defaultManagedAIProvider = managedAnthropicProvider;
export const defaultManagedChatModel = defaultManagedAIProvider.chatModels[0];
export type ManagedChatModel =
  (typeof managedAIProviderCatalog)[number]["chatModels"][number];
export const managedChatModels: readonly ManagedChatModel[] =
  managedAIProviderCatalog.flatMap(({ chatModels }) => chatModels);
export const managedModelIds = [
  ...new Set(
    managedAIProviderCatalog.flatMap(({ chatModels, fastModel }) => [
      ...chatModels,
      fastModel,
    ])
  ),
];

export function openAIRegionalValue<T>(
  awsRegion: string | null | undefined,
  values: { eu: T; global: T; us: T }
): T {
  if (awsRegion?.startsWith("eu-")) return values.eu;
  if (awsRegion?.startsWith("us-")) return values.us;
  return values.global;
}

const validatedBaseUrlProviderIds = new Set<ConfigurableAIProviderId>([
  "anthropic",
  "azure",
  "openai",
  "openai-compatible",
]);

type AIProviderEnvironment = {
  anthropicBedrockRegion?: string | null;
  anthropicApiKeyConfigured?: boolean | null;
  openAIApiKeyConfigured?: boolean | null;
};

export function getAIProvider(provider: AIProviderId) {
  const definition = aiProvidersById[provider];
  if (!definition) {
    throw new Error(`Unsupported AI provider: ${provider}`);
  }
  return definition;
}

function isAIProviderId(value: string): value is AIProviderId {
  return Object.hasOwn(aiProvidersById, value);
}

export function resolveAIProviderId(config?: {
  provider?: string | null;
  baseUrl?: string | null;
}): AIProviderId {
  if (config?.provider && isAIProviderId(config.provider)) {
    return config.provider;
  }
  if (config?.baseUrl) {
    return openAICompatible.id;
  }
  return anthropic.id;
}

export function resolveEnvironmentAIProviderId(
  environment: AIProviderEnvironment = {}
): AIProviderId {
  if (environment.anthropicBedrockRegion) {
    return bedrockAnthropic.id;
  }
  return anthropic.id;
}

export function resolveManagedAIProviderId(
  environment: AIProviderEnvironment = {}
): AIProviderId | null {
  const provider = resolveEnvironmentAIProviderId(environment);
  if (provider === bedrockAnthropic.id) {
    return provider;
  }
  if (provider === anthropic.id && environment.anthropicApiKeyConfigured) {
    return provider;
  }
  return null;
}

export function availableManagedChatModels(
  environment: AIProviderEnvironment = {}
): readonly ManagedChatModel[] {
  if (!resolveManagedAIProviderId(environment)) return [];
  const models: ManagedChatModel[] = [];
  models.push(...defaultManagedAIProvider.chatModels);
  if (environment.openAIApiKeyConfigured) {
    models.push(...managedOpenAIProvider.chatModels);
  }
  return models;
}

export function isManagedChatModel(value: string): value is ManagedChatModel {
  return managedChatModels.some((model) => model === value);
}

export function aiProviderEndpointUrl(config: {
  provider?: string | null;
  baseUrl?: string | null;
}): string | null {
  const definition = getAIProvider(resolveAIProviderId(config));
  const url = config.baseUrl?.trim() || definition.defaultBaseUrl;
  return url ? url : null;
}

type AIProviderCredentials = {
  provider?: string | null;
  baseUrl?: string | null;
  caCertificate?: string | null;
  connectedAppId?: string | null;
};

// API keys come from a connector, so the only sensitive value stored on the row
// is a custom CA certificate.
function packedSensitiveCredential(config: AIProviderCredentials, key: string) {
  if (key === "caCertificate") return config.caCertificate;
  return undefined;
}

export function hasRequiredAIProviderCredentials(
  config: AIProviderCredentials
) {
  const definition = getAIProvider(resolveAIProviderId(config));
  const unpacked = definition.unpackCredentials({
    baseUrl: config.baseUrl ?? "",
    apiKey: "",
    caCertificate: config.caCertificate ?? "",
  });

  return definition.credentialFields.every((field) => {
    if (!field.required) return true;
    if (field.sensitive && config.connectedAppId) return true;
    const value = field.sensitive
      ? packedSensitiveCredential(config, field.key)
      : unpacked[field.key];
    return Boolean(value?.trim());
  });
}

export function defaultChatModel(provider: AIProviderId) {
  return getAIProvider(provider).defaultChatModel;
}

function managedAIProviderModel(preference?: string | null) {
  for (const definition of managedAIProviderCatalog) {
    const chatModel = definition.chatModels.find(
      (model) => model === preference
    );
    if (chatModel) return { definition, chatModel };
  }
  return {
    definition: defaultManagedAIProvider,
    chatModel: defaultManagedChatModel,
  };
}

export function managedAIProviderIdForModel(
  defaultProvider: AIProviderId,
  preference?: string | null
): AIProviderId {
  const { definition } = managedAIProviderModel(preference);
  return definition.provider === anthropic.id
    ? defaultProvider
    : definition.provider;
}

export function resolveManagedModel(
  environment: AIProviderEnvironment,
  preference?: string | null
) {
  const { definition, chatModel } = managedAIProviderModel(preference);
  return {
    provider:
      definition.provider === anthropic.id
        ? resolveEnvironmentAIProviderId(environment)
        : definition.provider,
    chatModel,
    fastModel: definition.fastModel,
  };
}

export function resolveManagedChatModel(preference?: string | null) {
  return managedAIProviderModel(preference).chatModel;
}

export function defaultFastModel(provider: AIProviderId) {
  return getAIProvider(provider).defaultFastModel;
}

export function shouldValidateAIProviderBaseUrl(
  provider: ConfigurableAIProviderId
) {
  return validatedBaseUrlProviderIds.has(provider);
}
